import React, { useRef, Children } from 'react';
import { ShieldAlert, EyeOff, Clock, UserX } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
export function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <section className="py-24 px-4 sm:px-6 bg-neutral-900 text-neutral-50" data-id="element-156">
      <div className="max-w-4xl mx-auto" ref={ref} data-id="element-157">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-158">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 font-serif text-white" data-id="element-159">
            What you’re really fighting
          </h2>
          <p className="text-xl text-neutral-300 max-w-2xl mx-auto" data-id="element-160">
            You’re not fighting language. You’re not fighting paperwork.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-161">
          <motion.div variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="space-y-8" data-id="element-162">
            <motion.h3 variants={itemVariants} className="text-2xl font-bold text-amber-400" data-id="element-163">
              You’re fighting decision pressure under uncertainty.
            </motion.h3>
            <motion.p variants={itemVariants} className="text-lg text-neutral-300 leading-relaxed" data-id="element-164">
              Because when you don’t know the system, you pay to make the
              anxiety go away.
            </motion.p>

            <div className="space-y-6" data-id="element-165">
              <motion.div variants={itemVariants} className="flex gap-4" data-id="element-166">
                <div className="bg-neutral-800 p-3 rounded-lg h-fit" data-id="element-167">
                  <ShieldAlert className="h-6 w-6 text-red-400" data-id="element-168" />
                </div>
                <div data-id="element-169">
                  <h4 className="font-bold text-white text-lg" data-id="element-170">
                    You can’t tell a fair deal from a trap.
                  </h4>
                  <p className="text-neutral-400" data-id="element-171">
                    Without a baseline, every price looks "standard."
                  </p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex gap-4" data-id="element-172">
                <div className="bg-neutral-800 p-3 rounded-lg h-fit" data-id="element-173">
                  <UserX className="h-6 w-6 text-red-400" data-id="element-174" />
                </div>
                <div data-id="element-175">
                  <h4 className="font-bold text-white text-lg" data-id="element-176">
                    You can’t tell a confident person from a predatory person.
                  </h4>
                  <p className="text-neutral-400" data-id="element-177">
                    Scammers thrive on your confusion.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex gap-4" data-id="element-178">
                <div className="bg-neutral-800 p-3 rounded-lg h-fit" data-id="element-179">
                  <Clock className="h-6 w-6 text-red-400" data-id="element-180" />
                </div>
                <div data-id="element-181">
                  <h4 className="font-bold text-white text-lg" data-id="element-182">
                    You can’t tell a “temporary fix” from a long-term problem.
                  </h4>
                  <p className="text-neutral-400" data-id="element-183">
                    Quick decisions often become year-long regrets.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: 30
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="bg-neutral-800 p-8 rounded-xl border border-neutral-700 relative overflow-hidden" data-id="element-184">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-red-500/20 rounded-full blur-2xl" data-id="element-185"></div>
            <h3 className="text-xl font-bold text-white mb-4 font-serif" data-id="element-186">
              The "Foreigner Tax" Cycle
            </h3>
            <div className="space-y-4 relative z-10" data-id="element-187">
              <div className="flex items-center gap-3 text-neutral-300" data-id="element-188">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-700 flex items-center justify-center font-bold text-sm" data-id="element-189">
                  1
                </span>
                <span data-id="element-190">Feel overwhelmed & urgent</span>
              </div>
              <div className="w-0.5 h-4 bg-neutral-700 ml-4" data-id="element-191"></div>
              <div className="flex items-center gap-3 text-neutral-300" data-id="element-192">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-700 flex items-center justify-center font-bold text-sm" data-id="element-193">
                  2
                </span>
                <span data-id="element-194">Encounter pressure to decide</span>
              </div>
              <div className="w-0.5 h-4 bg-neutral-700 ml-4" data-id="element-195"></div>
              <div className="flex items-center gap-3 text-neutral-300" data-id="element-196">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-700 flex items-center justify-center font-bold text-sm" data-id="element-197">
                  3
                </span>
                <span data-id="element-198">Lack scripts or knowledge to stall</span>
              </div>
              <div className="w-0.5 h-4 bg-neutral-700 ml-4" data-id="element-199"></div>
              <div className="flex items-center gap-3 text-white font-medium bg-red-900/30 p-2 rounded -ml-2 w-full border border-red-900/50" data-id="element-200">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-800 flex items-center justify-center font-bold text-sm" data-id="element-201">
                  4
                </span>
                <span data-id="element-202">Agree to bad terms just to "settle it"</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}